// Recent activity — admin-only view of the clinic_audit_log (Week 6).
//
// Shows the last N clinical mutations across the whole clinic: who did what
// to whom, and when. Backed by GET /clinic/v1/activity which returns rows
// pre-joined with actor_name + patient_label so this page renders in a
// single fetch.
//
// The route is guarded twice: AppShell hides the nav entry when
// member.role !== 'admin', and the server returns 403 for non-admins even
// if a link is followed directly.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ClinicAuditLogEntryWithActor } from '@pamosi/shared';
import { ApiError, clinicApi } from '../lib/api';

export function ActivityScreen() {
    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['clinic-activity'],
        queryFn: () => clinicApi.activity(),
    });

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Recent activity</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Who touched what, across the clinic. Most recent first.
                    </p>
                </div>
                <button
                    type="button"
                    className="pm-btn-outline whitespace-nowrap"
                    onClick={() => void refetch()}
                    disabled={isFetching}
                >
                    {isFetching ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>
            )}

            {isError && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">
                        {error instanceof ApiError && error.code === 'forbidden'
                            ? 'Only admins can view activity.'
                            : 'Can’t load activity.'}
                    </div>
                </div>
            )}

            {data && data.entries.length === 0 && (
                <div className="pm-card p-8 text-center">
                    <div className="text-slate-800 font-medium">Nothing recorded yet</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Activity appears here as clinicians work.
                    </div>
                </div>
            )}

            {data && data.entries.length > 0 && (
                <div className="pm-card overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                        {data.entries.map((e) => (
                            <li key={e.id} className="px-4 py-3">
                                <Row entry={e} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Row({ entry }: { entry: ClinicAuditLogEntryWithActor }) {
    const when = formatWhen(entry.at);
    const actor = entry.actor_name?.trim() || 'Unknown clinician';
    const patient = entry.patient_label ?? 'Unknown patient';
    const summary = summarise(entry);
    return (
        <div>
            <div className="text-slate-900 text-sm">
                <span className="font-medium">{actor}</span>
                <span className="text-slate-400"> ({entry.actor_role}) </span>
                <span>{summary} </span>
                <span className="font-medium">{patient}</span>
            </div>
            <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{when}</span>
                {entry.encounter_id && (
                    <>
                        <span aria-hidden>·</span>
                        <Link
                            to={`/encounters/${entry.encounter_id}`}
                            className="text-brand-700 hover:underline"
                        >
                            Open encounter
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

// Human summary. Kept small — resource_type + action combos are finite.
function summarise(e: ClinicAuditLogEntryWithActor): string {
    const rt = e.resource_type.replace(/_/g, ' ');
    // Resource-specific overrides before falling through to the verb map.
    if (e.resource_type === 'admission') {
        switch (e.action) {
            case 'create': return 'admitted to observation —';
            case 'discharge': return 'discharged';
            case 'cancel': return 'cancelled the admission of';
            case 'update': return 'updated the admission of';
        }
    }
    if (e.resource_type === 'medication_administration') {
        return 'recorded a medication administration for';
    }
    if (e.resource_type === 'shift_report') {
        return e.action === 'update' ? 'edited a shift report' : 'posted a shift report';
    }
    switch (e.action) {
        case 'create': return `recorded a ${rt} for`;
        case 'update': return `updated a ${rt} for`;
        case 'finalize': return 'finalized the encounter for';
        case 'cancel': return `cancelled a ${rt} for`;
        case 'dispense': return 'dispensed a prescription for';
        case 'access': return `accessed the ${rt} of`;
        case 'revoke': return `revoked ${rt} for`;
        case 'discharge': return `discharged a ${rt} for`;
        case 'collect': return `collected a ${rt} for`;
        case 'process': return `processed a ${rt} for`;
        case 'result': return `recorded ${rt} results for`;
        case 'release': return `released ${rt} to`;
        case 'mark_paid': return `marked a ${rt} paid for`;
        case 'void': return `voided a ${rt} for`;
        case 'verify': return `verified ${rt} for`;
        case 'reject': return `rejected ${rt} for`;
        case 'deliver': return `delivered a ${rt} to`;
        default: return `${e.action} ${rt} for`;
    }
}

function formatWhen(at: string): string {
    const t = new Date(at).getTime();
    if (!Number.isFinite(t)) return at;
    const diffMin = Math.max(0, Math.round((Date.now() - t) / 60_000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(at).toLocaleDateString();
}
